import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack } from "@mui/material";
import { useWorkerLoggedIn } from "@src/appV2/Agents/api/useWorkerLoggedIn";
import { useLegacyStateContext } from "@src/appV2/LegacyStateBridge/context";
import { APP_V2_USER_EVENTS, ProgressDialog, useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { MuiThemeProvider } from "@src/appV2/lib/Theme";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { fetchWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { Button } from "../../../redesign/components/Button";
import { useValidateAuthOtp } from "../../api/useValidateAuthOtp";
import {
  LoginConfirmationDialog,
  PhoneNumberExistsDialog,
  SignUpInsteadDialog,
  VerifyOtpDialog,
} from "../Dialogs";
import { useAutoLoginFromUrl } from "../hooks/useAutoLoginFromUrl";
import { useLoginSignUpUiConfiguration } from "../hooks/useLoginSignUpUiConfiguration";
import { AuthFormContent, AuthMode, useAuthMode } from "../libs";
import { useAuthWorkflow } from "../libs/authUtils";
import { DialogAuthMethod } from "../types";
import { AuthMethod, type LoginSignUpFormFields } from "../types";
import { AuthFormFields } from "./FormFields";
import { getFormSchema } from "./schema";

export function LoginSignUpForm(): ReactElement {
  const {
    initiateLogin,
    initiateSignUp,
    resendOtp,
    otpRequestIsLoading,
    loginLinkRequestIsLoading,
    signUpInsteadPhoneModalState,
    signUpInsteadEmailModalState,
    otpModalState,
    emailConfirmationModalState,
    authLoadingMessage,
    phoneNumberExistsModalState,
    authMethod,
    setAuthMethod,
    identityProvider,
    cognitoIsSignup,
  } = useAuthWorkflow();

  const { mutateAsync: validateAuthOtp, isLoading: isValidatingAuthOtp } = useValidateAuthOtp();

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const queryClient = useQueryClient();

  const history = useHistory();

  const { hideFooterAndSecondaryButtonsOnKeyboardOpen } = useLoginSignUpUiConfiguration();

  const isKeyboardOpen = useIsKeyboardOpen();

  const shouldShowFooterAndSecondaryButtons = hideFooterAndSecondaryButtonsOnKeyboardOpen
    ? !isKeyboardOpen
    : true;

  const searchParams = useSearchParams<{ phoneNumber?: string }>();
  const phoneNumberFromUrl = searchParams.phoneNumber;

  const { authMode, authFormContent, authModeIsLogin } = useAuthMode();
  const formMethods = useForm<LoginSignUpFormFields>({
    defaultValues: {
      phoneNumber: phoneNumberFromUrl ?? "",
      email: "",
    },
    resolver: zodResolver(getFormSchema(authMode, authMethod)),
  });
  const { formState, handleSubmit, watch, setError, clearErrors, reset, setValue } = formMethods;

  useEffect(() => {
    setAuthMethod(AuthMethod.PHONE);
    clearErrors();
  }, [authMode, clearErrors, setAuthMethod]);

  const phoneNumber = watch("phoneNumber");
  const email = watch("email");
  const { showErrorToast, showInfoToast } = useToast();

  const handleMaxLengthAttempt = useCallback(() => {
    setError("phoneNumber", {
      type: "manual",
      message: "Phone number cannot be more than 10 digits.",
    });
  }, [setError]);

  const { mutateAsync: sendAgentLoggedIn } = useWorkerLoggedIn();

  const { setLegacyIsSignUp } = useLegacyStateContext();

  useAutoLoginFromUrl({
    phoneNumberFromUrl,
    authMethod,
    authMode,
    authModeIsLogin,
    formState,
    formMethods,
    setAuthMethod,
    setLegacyIsSignUp,
    initiateLogin,
  });

  const handleNavigateToSignUp = (params?: { phoneNumberToUse?: string }) => {
    const { phoneNumberToUse } = params ?? {};
    setAuthMethod(AuthMethod.PHONE);
    setValue("phoneNumber", phoneNumberToUse ?? "");
    setValue("email", "");
    clearErrors();
    history.push(AuthMode.SIGN_UP);
    logEvent(APP_V2_USER_EVENTS.SELECT_SIGN_UP_INSTEAD);
  };

  const handleClearForm = () => {
    history.push(AuthMode.LOGIN);
    reset({
      phoneNumber: "",
      email: "",
    });
    clearErrors();
    logEvent(APP_V2_USER_EVENTS.CLOSE_SIGN_UP_INSTEAD_DIALOG);
  };

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
        onSubmit={handleSubmit(async (formData) => {
          try {
            setLegacyIsSignUp(authMode);
            switch (authMode) {
              case AuthMode.LOGIN: {
                if (authMethod === AuthMethod.PHONE) {
                  await initiateLogin({
                    phoneNumber: formData.phoneNumber,
                    email: "",
                  });

                  logEvent(APP_V2_USER_EVENTS.SUBMIT_LOGIN_FORM_WITH_PHONE_NUMBER, {
                    phoneNumber: formData.phoneNumber,
                  });
                } else {
                  await initiateLogin({
                    phoneNumber: "",
                    email: formData.email,
                  });
                  logEvent(APP_V2_USER_EVENTS.SUBMIT_LOGIN_FORM_WITH_EMAIL, {
                    email: formData.email,
                  });
                }

                return true;
              }

              case AuthMode.SIGN_UP: {
                await initiateSignUp(formData);
                logEvent(APP_V2_USER_EVENTS.SUBMIT_SIGNUP_FORM, {
                  phoneNumber: formData.phoneNumber,
                  email: formData.email,
                });
                return true;
              }

              default: {
                throw new Error(`Unknown AuthMode: ${authMode as string}`);
              }
            }
          } catch (error) {
            showErrorToast("Failed to log in.");
            logError(APP_V2_APP_EVENTS.AUTH_ON_SUBMIT_FAILURE, { error });
            return false;
          }
        })}
      >
        <Stack
          direction="column"
          spacing={6}
          justifyContent={authModeIsLogin ? "space-between" : "flex-start"}
          flex={1}
        >
          <Stack direction="column" spacing={6}>
            <AuthFormFields authMethod={authMethod} onMaxLengthAttempt={handleMaxLengthAttempt} />
            <Stack direction="column" spacing={isRebrandingEnabled() ? 6 : 4}>
              <Button
                fullWidth
                variant="brand"
                type="submit"
                disabled={formState.isSubmitting}
                size="large"
              >
                {authFormContent[AuthFormContent.SUBMIT_BUTTON]}
              </Button>
              {shouldShowFooterAndSecondaryButtons ? (
                <Stack
                  alignItems="center"
                  sx={{
                    visibility: authModeIsLogin ? "visible" : "hidden",
                  }}
                >
                  <TextButton
                    size="large"
                    onClick={() => {
                      const newAuthMethod =
                        authMethod === AuthMethod.PHONE ? AuthMethod.EMAIL : AuthMethod.PHONE;

                      logEvent(APP_V2_USER_EVENTS.SWITCH_LOGIN_METHOD, {
                        from: authMethod,
                        to: newAuthMethod,
                      });

                      setAuthMethod(newAuthMethod);
                    }}
                  >
                    <Text semibold sx={(theme) => ({ color: theme.palette.link?.primary })}>
                      {authMethod === AuthMethod.PHONE
                        ? "Use email to log in instead"
                        : "Use phone number to log in instead"}
                    </Text>
                  </TextButton>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
          {shouldShowFooterAndSecondaryButtons && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              pb={authModeIsLogin ? 6 : 0}
            >
              <Text semibold sx={{ color: (theme) => theme.palette.text.secondary }}>
                {authFormContent[AuthFormContent.SWITCH_AUTH_MODE_INSTRUCTIONS]}{" "}
              </Text>
              <TextButton
                size="large"
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.SWITCH_BETWEEN_LOGIN_SIGNUP, {
                    from: authMode,
                    to: authMode === AuthMode.LOGIN ? AuthMode.SIGN_UP : AuthMode.LOGIN,
                  });
                  history.push({
                    pathname: authMode === AuthMode.LOGIN ? AuthMode.SIGN_UP : AuthMode.LOGIN,
                    state: history.location.state,
                  });
                }}
              >
                <Text semibold sx={(theme) => ({ color: theme.palette.link?.primary })}>
                  {authFormContent[AuthFormContent.SWITCH_AUTH_MODE_CTA]}
                </Text>
              </TextButton>
            </Stack>
          )}
        </Stack>
      </Box>

      <MuiThemeProvider>
        <SignUpInsteadDialog
          modalState={signUpInsteadPhoneModalState}
          authMethod={DialogAuthMethod.PHONE}
          userInput={phoneNumber}
          onClearForm={() => {
            handleClearForm();
            signUpInsteadPhoneModalState.closeModal();
          }}
          onNavigateToSignUp={() => {
            handleNavigateToSignUp({ phoneNumberToUse: phoneNumber });
            signUpInsteadPhoneModalState.closeModal();
          }}
        />
        <SignUpInsteadDialog
          modalState={signUpInsteadEmailModalState}
          authMethod={DialogAuthMethod.EMAIL}
          userInput={email}
          onClearForm={() => {
            handleClearForm();
            signUpInsteadEmailModalState.closeModal();
          }}
          onNavigateToSignUp={() => {
            handleNavigateToSignUp({ phoneNumberToUse: "" });
            signUpInsteadEmailModalState.closeModal();
          }}
        />

        <VerifyOtpDialog
          modalState={otpModalState}
          phoneNumber={phoneNumber}
          authMode={authMode}
          identityProvider={identityProvider}
          cognitoIsSignup={cognitoIsSignup}
          resendOtp={resendOtp}
          validateOtp={async (input) => await validateAuthOtp({ ...input, identityProvider })}
          isValidatingOtp={isValidatingAuthOtp}
          isAuthenticating={isAuthenticating}
          onSuccess={async (params) => {
            const { agentDeletionRequestCancelled } = params;

            setIsAuthenticating(true);
            try {
              /**
               * For Cognito signups, the user doesn't exist in the CBH backend yet.
               * Close the OTP modal and navigate to onboarding where the backend user
               * will be created during the onboarding flow.
               */
              if (cognitoIsSignup) {
                otpModalState.closeModal();
                history.push("/home/onboarding/agentEmail");
                logEvent(APP_V2_USER_EVENTS.SUBMIT_AUTH_OTP_FORM_SUCCESS);
                return;
              }

              // Cognito session is already established after OTP validation.
              // useIsAuthenticated will react to the Cognito user change.
              try {
                await sendAgentLoggedIn();
              } catch (error) {
                logError(APP_V2_APP_EVENTS.AGENT_PROFILE_LOGGED_IN_ERROR, { error });
              }

              // Pre-fetch /worker so the loader stays up until routing knows
              // whether to send the user home, to onboarding, or to reactivation.
              // Errors are surfaced by useGetWorker consumers; we only need the
              // call to settle before dropping the loader.
              await queryClient
                .fetchQuery({
                  queryKey: [GET_WORKER_QUERY_KEY],
                  queryFn: ({ signal }) => fetchWorker(signal),
                })
                .catch(() => undefined);

              if (agentDeletionRequestCancelled) {
                showInfoToast(
                  "Your deletion request has been cancelled because you logged in. If you still wish to delete your account, please submit a new deletion request from the Profile page."
                );
              }

              logEvent(APP_V2_USER_EVENTS.SUBMIT_AUTH_OTP_FORM_SUCCESS);
            } finally {
              setIsAuthenticating(false);
            }
          }}
        />
        <LoginConfirmationDialog modalState={emailConfirmationModalState} email={email} />

        <PhoneNumberExistsDialog
          modalState={phoneNumberExistsModalState}
          phoneNumber={phoneNumber}
          onLogin={() => {
            setLegacyIsSignUp(AuthMode.LOGIN);
            void initiateLogin({ phoneNumber, email: "" });
          }}
          onUseDifferentPhoneNumber={() => {
            reset({
              phoneNumber: "",
              email,
            });
          }}
        />

        <ProgressDialog
          modalIsOpen={otpRequestIsLoading || loginLinkRequestIsLoading}
          messageContent={authLoadingMessage}
        />
      </MuiThemeProvider>
    </FormProvider>
  );
}
